import { useEffect, useState } from "react";
import { CalendarDays, Check, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

const Slot = ({ s, i, onPatch }) => {
  const [idea, setIdea] = useState(s.idea);
  useEffect(() => setIdea(s.idea), [s.idea]);
  return (
    <li className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${s.done ? "bg-leaf/10 border-leaf/40" : "bg-white border-border"}`} data-testid={`plan-slot-${i}`}>
      <button type="button" onClick={() => onPatch(i, { done: !s.done })} aria-label={s.done ? "Mark as not posted" : "Mark as posted"} data-testid={`plan-tick-${i}`}
        className={`h-5 w-5 shrink-0 rounded border flex items-center justify-center transition-colors ${s.done ? "bg-leaf border-leaf text-white" : "border-navy/30 hover:border-pinkb"}`}>{s.done && <Check className="h-3.5 w-3.5" />}</button>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-mono uppercase truncate"><span className="text-muted-foreground">{fmt(s.date)}</span><span className="text-pinkb"> · {s.pillar}</span></div>
        <Input value={idea} onChange={(e) => setIdea(e.target.value.slice(0, 200))} onBlur={() => idea.trim() && idea !== s.idea && onPatch(i, { idea })} data-testid={`plan-idea-${i}`}
          className={`h-8 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 ${s.done ? "line-through text-muted-foreground" : ""}`} />
      </div>
    </li>
  );
};

export const PostPlanner = () => {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  useEffect(() => { api.get("/me/post-plan").then((r) => setPlan(r.data)).catch(() => setPlan({ slots: [] })); }, []);
  const build = async () => {
    setAsk(false);
    setBusy(true);
    try { const r = await api.post("/me/post-plan"); setPlan(r.data); toast.success("Your next 4 weeks are planned"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const patch = async (i, body) => {
    try { const r = await api.patch(`/me/post-plan/${i}`, body); setPlan(r.data); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!plan) return null;
  const done = plan.slots.filter((s) => s.done).length;
  const weeks = [0, 1, 2, 3].map((w) => plan.slots.slice(w * 3, w * 3 + 3));
  return (
    <div className="mt-8 border-t border-border pt-6" data-testid="post-planner">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-serif uppercase text-xl text-navy flex items-center gap-2"><CalendarDays className="h-5 w-5 text-pinkb" />Your 4-week post planner</h3>
          <p className="text-sm text-muted-foreground mt-1">Three posts a week, Monday, Wednesday and Friday, rotating through your pillars and post ideas. Tick each one off once it's live.</p>
        </div>
        {plan.slots.length > 0 && <span className="text-xs font-mono text-navy" data-testid="plan-progress">{done}/{plan.slots.length} posted</span>}
      </div>
      {plan.slots.length > 0 && (
        <div className="mt-5 grid gap-5 md:grid-cols-2">
          {weeks.map((ws, w) => (
            <div key={w} data-testid={`plan-week-${w}`}>
              <div className="eyebrow mb-2">Week {w + 1}</div>
              <ul className="space-y-2">{ws.map((s, j) => <Slot key={w * 3 + j} s={s} i={w * 3 + j} onPatch={patch} />)}</ul>
            </div>
          ))}
        </div>
      )}
      <Button onClick={() => (plan.slots.length ? setAsk(true) : build())} disabled={busy} variant={plan.slots.length ? "outline" : "default"} data-testid="plan-build"
        className={`mt-5 rounded-full ${plan.slots.length ? "" : "bg-pinkb hover:bg-pinkb/90 text-white"}`}>
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}{plan.slots.length ? "Plan the next 4 weeks" : "Build my 4-week plan"}
      </Button>
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent data-testid="plan-rebuild-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Start a fresh 4-week plan?</AlertDialogTitle>
            <AlertDialogDescription>Your new plan starts this week. Your current ticks and edits will be cleared.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="plan-rebuild-cancel">Keep my plan</AlertDialogCancel>
            <AlertDialogAction onClick={build} data-testid="plan-rebuild-confirm" className="bg-navy hover:bg-navy/90">Plan the next 4 weeks</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
